'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, ArrowLeft, RefreshCw, FileX, CheckSquare, AlertTriangle, Clock } from 'lucide-react';
import { knowledgeBaseApi, type SyncTaskStatus } from '../../_lib/api';

const statusVariantMap: Record<SyncTaskStatus['status'], 'default' | 'secondary' | 'destructive' | 'outline'> = {
  PENDING: 'outline',
  SYNCING: 'secondary',
  COMPLETED: 'default',
  FAILED: 'destructive',
};

export default function KnowledgeBaseSyncTaskPage() {
  const { t } = useTranslation();
  const { isReady } = useAuthGuard();
  const router = useRouter();
  const params = useParams();
  const taskId = useMemo(() => {
    const raw = params?.taskId;
    if (Array.isArray(raw)) {
      return raw[0] || '';
    }
    return raw ?? '';
  }, [params]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [task, setTask] = useState<SyncTaskStatus | null>(null);
  const [lastCheckedAt, setLastCheckedAt] = useState<Date | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!taskId) {
      setError(t.knowledgeBase.syncTaskDetail.taskNotFound);
      setTask(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await knowledgeBaseApi.getSyncTaskStatus(taskId);
      setTask(response);
      setLastCheckedAt(new Date());
    } catch (err) {
      console.error('Failed to fetch sync task status:', err);
      setError(t.knowledgeBase.syncTaskDetail.loadFailed);
      setTask(null);
    } finally {
      setLoading(false);
    }
  }, [taskId, t]);

  useEffect(() => {
    if (!isReady) return;
    fetchStatus();
  }, [isReady, fetchStatus]);

  if (!isReady) {
    return (
      <div className="flex h-full items-center justify-center bg-[#f4f5f7]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-[#f4f5f7]">
      <div className="flex-shrink-0 border-b border-gray-200 bg-white px-6 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5"
              onClick={() => router.push('/knowledge-base/sync-tasks')}
            >
              <ArrowLeft className="h-4 w-4" />
              {t.knowledgeBase.syncTaskDetail.back}
            </Button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">
                {t.knowledgeBase.syncTaskDetail.title}
              </h1>
              <p className="text-sm text-gray-500">
                {t.knowledgeBase.syncTaskDetail.subtitle}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={fetchStatus}
            disabled={loading}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {t.knowledgeBase.syncTaskDetail.refresh}
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6">
        <Card className="border-gray-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-gray-900">
              {t.knowledgeBase.syncTaskDetail.overviewTitle}
            </CardTitle>
            <CardDescription className="text-sm text-gray-500">
              {t.knowledgeBase.syncTaskDetail.overviewDescription}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t.knowledgeBase.syncTaskDetail.loading}
              </div>
            )}

            {!loading && error && (
              <div className="text-sm text-red-600">
                {error}
              </div>
            )}

            {!loading && !error && !task && (
              <div className="text-sm text-gray-500">
                {t.knowledgeBase.syncTaskDetail.taskNotFound}
              </div>
            )}

            {!loading && task && (
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500">{t.knowledgeBase.syncTaskDetail.fields.taskId}</p>
                    <p className="text-sm font-medium text-gray-900 break-all">{task.taskId}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{t.knowledgeBase.syncTaskDetail.fields.status}</p>
                    <Badge variant={statusVariantMap[task.status]}>
                      {t.knowledgeBase.syncTaskDetail.status[task.status]}
                    </Badge>
                  </div>
                  {lastCheckedAt && (
                    <div>
                      <p className="text-xs text-gray-500">{t.knowledgeBase.syncTaskDetail.fields.lastChecked}</p>
                      <p className="text-sm text-gray-700">
                        {lastCheckedAt.toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
                <div className="grid gap-3">
                  <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3">
                    <span className="text-sm text-gray-600">{t.knowledgeBase.syncTaskDetail.fields.totalItems}</span>
                    <span className="text-sm font-semibold text-gray-900">{task.totalItems}</span>
                  </div>
                  <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3">
                    <span className="text-sm text-gray-600">{t.knowledgeBase.syncTaskDetail.fields.processedItems}</span>
                    <div className="flex items-center justify-end gap-2">
                      <span className="min-w-[3rem] text-right text-sm font-semibold text-gray-900">{task.processedItems}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 min-w-[150px] justify-center gap-1.5 px-2"
                        onClick={() => router.push(`/knowledge-base/sync-tasks/${taskId}/processed`)}
                        disabled={task.processedItems === 0}
                      >
                        <CheckSquare className="h-4 w-4" />
                        {t.knowledgeBase.syncTaskDetail.viewProcessed}
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3">
                    <span className="text-sm text-gray-600">{t.knowledgeBase.syncTaskDetail.fields.processingItems}</span>
                    <div className="flex items-center justify-end gap-2">
                      <span className="min-w-[3rem] text-right text-sm font-semibold text-gray-900">{task.processingItems}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 min-w-[150px] justify-center gap-1.5 px-2"
                        onClick={() => router.push(`/knowledge-base/sync-tasks/${taskId}/processing`)}
                        disabled={task.processingItems === 0}
                      >
                        <Clock className="h-4 w-4" />
                        {t.knowledgeBase.syncTaskDetail.viewProcessing}
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3">
                    <span className="text-sm text-gray-600">{t.knowledgeBase.syncTaskDetail.fields.failedItems}</span>
                    <div className="flex items-center justify-end gap-2">
                      <span className="min-w-[3rem] text-right text-sm font-semibold text-gray-900">{task.failedItems}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 min-w-[150px] justify-center gap-1.5 px-2"
                        onClick={() => router.push(`/knowledge-base/sync-tasks/${taskId}/failed`)}
                        disabled={task.failedItems === 0}
                      >
                        <AlertTriangle className="h-4 w-4" />
                        {t.knowledgeBase.syncTaskDetail.viewFailed}
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3">
                    <span className="text-sm text-gray-600">{t.knowledgeBase.syncTaskDetail.fields.skippedItems}</span>
                    <div className="flex items-center justify-end gap-2">
                      <span className="min-w-[3rem] text-right text-sm font-semibold text-gray-900">{task.skippedItems}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 min-w-[150px] justify-center gap-1.5 px-2"
                        onClick={() => router.push(`/knowledge-base/sync-tasks/${taskId}/skipped`)}
                        disabled={task.skippedItems === 0}
                      >
                        <FileX className="h-4 w-4" />
                        {t.knowledgeBase.syncTaskDetail.viewSkipped}
                      </Button>
                    </div>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3">
                      <span className="text-sm text-gray-600">{t.knowledgeBase.syncTaskDetail.fields.processedTokens}</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {Number.isFinite(task.processedTokens) ? task.processedTokens.toLocaleString() : '-'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3">
                      <span className="text-sm text-gray-600">{t.knowledgeBase.syncTaskDetail.fields.processedChunks}</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {Number.isFinite(task.processedChunks) ? task.processedChunks.toLocaleString() : '-'}
                      </span>
                    </div>
                  </div>
                </div>
                {task.error && (
                  <div className="md:col-span-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                    <p className="text-xs text-red-600">{t.knowledgeBase.syncTaskDetail.fields.error}</p>
                    <p className="text-sm text-red-700">{task.error}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
